import { memo, useEffect, useMemo, useRef, useState } from 'react'
import { inbox } from './api'
import type { Habit } from './api'
import { partition, recentOpens, senderRule, usePrefs, type AccountInfo, type Conversation, type MailState } from './store'
import { age, Avatar, senderCheck, Tile } from './visuals'
import { Glyph, Kbd } from './glyphs'
import { TAG_FOR_REASON } from './themes'
import { useSearch } from './search'

export type Tab = 'important' | 'other'

export interface Row { kind: 'row'; item: Conversation; section: string; tier?: Tier; pending?: boolean; /** Other only: the group it was filed under (category or rule label). */ label?: string }
/** How much room a row in Other gets: lead rows are tall with a preview line, quiet rows are small and faint. */
export type Tier = 'xl' | 'lead' | 'normal' | 'quiet'
const rank = (item: Conversation) => item.decision ? item.decision.importance * 0.6 + item.decision.urgency * 0.4 : 0.3
export function tierOf(item: Conversation): Tier {
  const d = item.decision
  if (!d) return 'normal'
  const r = rank(item)
  if (r >= 0.5 || d.critical) return 'lead'
  if (r < 0.2) return 'quiet'
  return 'normal'
}
/** What you actually do overrides Jev's size in Other: senders you open get room, senders you never open shrink. */
function habitTier(h: Habit | undefined): Tier | null {
  if (!h) return null
  const acted = h.opened + h.ignored
  if (h.replied >= 2 || (h.opened >= 3 && h.opened / acted >= 0.6)) return 'lead'
  if (h.ignored >= 5 && h.opened === 0) return 'quiet'
  return null
}
export interface Heading { kind: 'heading'; title: string; count: number; note?: string; hidden?: number }

/** The rows on screen, in order, for the current tab and account filter. */
export function useRows(mail: MailState, tab: Tab, accountId: string | null, expanded: Set<string> = new Set()) {
  const search = useSearch()
  return useMemo(() => {
    // Searching replaces both tabs with one newest-first list of matches.
    if (search.results) {
      const mine = (items: Conversation[]) => accountId ? items.filter(item => item.sourceId === accountId) : items
      const parts = partition(mail.conversations, mail.triage.enabled, mail.ownAddresses, mail.rules)
      const list: Array<Row | Heading> = []
      // Best: Jev-ranked results from every provider, sized by how well they match.
      const ranked = search.deep && search.deep.length ? search.deep.filter(r => !accountId || r.item.sourceId === accountId) : []
      const asked = ranked.length ? [] : search.ai && search.ai.length ? mine(search.ai) : []
      const shown = new Set([...ranked.map(r => r.item.key), ...asked.map(item => item.key)])
      const plain = mine(search.results).filter(item => !shown.has(item.key))
      if (ranked.length) {
        const title = search.deepSummary ? `✦ ${search.deepSummary}` : '✦ Best matches'
        list.push({ kind: 'heading', title, count: ranked.length, note: 'All mail · ranked by Jev' })
        for (const r of ranked) list.push({ kind: 'row', item: r.item, section: 'ask', tier: r.score === null ? 'normal' : r.score >= 0.8 ? 'xl' : r.score >= 0.5 ? 'lead' : 'normal' })
      } else if (asked.length) {
        list.push({ kind: 'heading', title: `✦ ${search.aiSummary || 'Jev found'}`, count: asked.length, note: search.deepLoading ? 'Searching all mail…' : 'Jev search' })
        for (const item of asked) list.push({ kind: 'row', item, section: 'ask' })
      }
      if (plain.length || (!asked.length && !ranked.length)) {
        const busy = search.loading || search.aiLoading || search.deepLoading
        const title = search.loading ? 'Searching…' : (asked.length || ranked.length) ? `Also matching “${search.query.trim()}”` : plain.length ? 'Results' : busy ? 'Searching all mail…' : 'No results'
        list.push({ kind: 'heading', title, count: plain.length, ...(!asked.length && !ranked.length && plain.length && search.deepLoading ? { note: 'Searching all mail…' } : {}) })
      }
      for (const item of plain) list.push({ kind: 'row', item, section: 'search' })
      return { list, rows: list.filter((entry): entry is Row => entry.kind === 'row'), importantCount: parts.importantCount, otherCount: parts.otherCount }
    }
    const visible = accountId ? mail.conversations.filter(item => item.sourceId === accountId) : mail.conversations
    const parts = partition(visible, mail.triage.enabled, mail.ownAddresses, mail.rules, mail.habits)
    const list: Array<Row | Heading> = []
    const add = (title: string, items: Conversation[], note?: string, limit = Infinity) => {
      if (!items.length) return
      const shown = items.slice(0, limit)
      list.push({ kind: 'heading', title, count: items.length, ...(note ? { note } : {}), hidden: items.length - shown.length })
      for (const item of shown) list.push({ kind: 'row', item, section: title })
    }
    if (tab === 'important') {
      add('Needs reply', parts.needsReply, 'Most urgent first')
      add('Worth knowing', parts.worth)
    } else {
      // One list, newest first. Importance shows as row size instead of grouping.
      const labelOf = new Map(parts.other.flatMap(([label, items]) => items.map(item => [item, label] as const)))
      const all = [...labelOf.keys()].sort((a, b) => b.lastMessageAt.localeCompare(a.lastMessageAt))
      const waiting = new Set(parts.sorting)
      for (const item of all) {
        // Mail you sent to Other with a sender or domain rule ("don't care") stays small, however Jev scored it.
        const demoted = senderRule(mail.rules, item.lead.from.email)?.action === 'other'
        list.push({ kind: 'row', item, section: 'other', tier: demoted ? 'quiet' : habitTier(mail.habits[item.lead.from.email.toLowerCase()]) ?? tierOf(item), label: labelOf.get(item)!, ...(waiting.has(item) ? { pending: true } : {}) })
      }
    }
    return { list, rows: list.filter((entry): entry is Row => entry.kind === 'row'), importantCount: parts.importantCount, otherCount: parts.otherCount }
  }, [mail.conversations, mail.triage.enabled, mail.ownAddresses, mail.rules, mail.habits, tab, accountId, expanded, search])
}

/** One calm sentence per conversation: who, what, why, how long. On phones it's an iOS-style cell you can swipe. */
interface RowHandlers { open: (key: string) => void; select: (key: string) => void; finish: (key: string, kind: 'done' | 'snooze') => void }
/** Rows only re-render when their own data changes: handlers come through one stable object. */
const MailRow = memo(function MailRow({ item, tier = 'normal', pending = false, label, selected, leaving, entering, delay, density, handlers }: { item: Conversation; tier?: Tier; pending?: boolean; label?: string; selected: boolean; leaving: boolean; entering: boolean; delay: number; density: string; handlers: RowHandlers }) {
  const onOpen = () => handlers.open(item.key), onSelect = () => handlers.select(item.key), onFinish = (kind: 'done' | 'snooze') => handlers.finish(item.key, kind)
  const ref = useRef<HTMLDivElement>(null)
  useEffect(() => { if (selected) ref.current?.scrollIntoView({ block: 'nearest' }) }, [selected])
  const drag = useRef<{ x: number; y: number; active: boolean; moved: boolean } | null>(null)
  const [dx, setDx] = useState(0)
  const d = item.decision
  const strong = !!d?.important || item.unread
  const from = item.lead.from
  const sender = from.name || from.email.split('@')[0] || from.email
  // "Amazon.com via Inbound": the name that matters first, the forwarder quietly after it.
  const via = /^(.+?)\s+via\s+(.+)$/i.exec(sender)
  // The tag explains where the row is: in Important, why it matters; in Other, which group it was filed under (big rows only).
  const inOther = label !== undefined
  const tagText = pending ? 'sorting…' : inOther ? (tier === 'lead' ? label : '') : d?.important ? d.reason : ''
  const count = item.messageCount > 1 ? item.messageCount : 0
  const SWIPE = 84
  return (
    <div className={`row-wrap${dx ? ' swiping' : ''}`}>
      <div className="swipe-bg">
        <span className={`swipe-snooze${dx > SWIPE ? ' armed' : ''}`}><Glyph name="snooze" size={20} />Snooze</span>
        <span className={`swipe-done${dx < -SWIPE ? ' armed' : ''}`}>Done<Glyph name="done" size={20} /></span>
      </div>
      <div ref={ref} className={`row tier-${tier}${selected ? ' selected' : ''}${strong ? ' strong' : ''}${item.unread ? ' unread' : ''}${leaving ? ' leaving' : ''}${entering ? ' entering' : ''}`}
        style={{ ...(entering ? { animationDelay: `${delay}ms` } : {}), ...(dx ? { transform: `translateX(${dx}px)` } : {}) }}
        onClick={() => { if (drag.current?.moved) return; onOpen() }} onMouseEnter={onSelect} role="option" aria-selected={selected}
        onTouchStart={event => { const t = event.touches[0]!; drag.current = { x: t.clientX, y: t.clientY, active: false, moved: false } }}
        onTouchMove={event => {
          const state = drag.current, t = event.touches[0]!
          if (!state) return
          const ddx = t.clientX - state.x, ddy = t.clientY - state.y
          if (!state.active && Math.abs(ddx) > 12 && Math.abs(ddx) > Math.abs(ddy) * 1.4) state.active = state.moved = true
          if (state.active) setDx(Math.max(-160, Math.min(160, ddx)))
        }}
        onTouchEnd={() => {
          if (dx < -SWIPE) onFinish('done'); else if (dx > SWIPE) onFinish('snooze')
          setDx(0)
          setTimeout(() => { drag.current = null }, 0)
        }}>
        <span className="row-dot" aria-hidden="true" />
        <Avatar name={from.name} email={from.email} person={d ? d.sender === 'person' : true} {...(item.account ? { brand: item.account.brand, tone: item.account.tone } : {})} size={tier === 'xl' ? (density === 'comfortable' ? 46 : 40) : tier === 'lead' ? (density === 'comfortable' ? 38 : 30) : tier === 'quiet' ? (density === 'comfortable' ? 24 : 16) : density === 'comfortable' ? 32 : 22} />
        <div className="row-text">
          <span className="row-sender" title={from.email}><span className="row-name">{via ? via[1] : sender}</span>{via && <span className="row-via">via {via[2]}</span>}{count ? <span className="row-count">{count}</span> : null}</span>
          <span className="row-subject">{item.subject}</span>
          <span className="row-preview">{item.lead.preview}</span>
        </div>
        <span className={`row-tag${!inOther && d?.critical ? ' critical' : ''}${pending ? ' pending' : ''}`} data-tag={!inOther && d?.important && d.reason ? TAG_FOR_REASON[d.reason] : undefined}>{tagText}</span>
        <span className="row-age">{age(item.lastMessageAt)}</span>
      </div>
    </div>
  )
})

const FIRST_ROWS = 40
const MORE_ROWS = 160

export function InboxList({ mail, tab, cells, status, expanded, onToggleGroup, accountId, selected, leaving, onSelect, onOpen, onLoadMore, onFinish, onTab, onSettings }: {
  mail: MailState; tab: Tab; cells?: boolean; status?: string; expanded: Set<string>; onToggleGroup: (label: string) => void; accountId: string | null; selected: string | null; leaving: Set<string>
  onFinish: (key: string, kind: 'done' | 'snooze') => void; onTab: (tab: Tab) => void; onSettings: () => void
  onSelect: (key: string) => void; onOpen: (key: string) => void; onLoadMore: () => void
}) {
  const { list, importantCount, otherCount } = useRows(mail, tab, accountId, expanded)
  const searching = !!useSearch().results
  const { density } = usePrefs()
  const end = useRef<HTMLDivElement>(null)
  // Rows fade in the first time they appear: a short cascade on load, a single soft arrival for new mail.
  const seen = useRef(new Set<string>())
  const firstPaint = useRef(true)
  const view = `${tab}:${accountId ?? ''}`
  const lastView = useRef(view)
  if (lastView.current !== view) { lastView.current = view; seen.current = new Set(); firstPaint.current = true }
  // Stable handlers for memoized rows (the parent's callbacks change identity on every render).
  const latest = useRef({ onOpen, onSelect, onFinish })
  latest.current = { onOpen, onSelect, onFinish }
  const handlers = useMemo<RowHandlers>(() => ({ open: key => latest.current.onOpen(key), select: key => latest.current.onSelect(key), finish: (key, kind) => latest.current.onFinish(key, kind) }), [])
  // Long lists render the first screenful at once and the rest over the next few frames,
  // so switching tabs paints immediately instead of building hundreds of rows first.
  const rowTotal = list.reduce((n, entry) => n + (entry.kind === 'row' ? 1 : 0), 0)
  const [limit, setLimit] = useState(FIRST_ROWS)
  const limitView = useRef(view)
  if (limitView.current !== view) { limitView.current = view; if (limit !== FIRST_ROWS) setLimit(FIRST_ROWS) }
  useEffect(() => {
    if (limit >= rowTotal) return
    const id = window.setTimeout(() => setLimit(current => current + MORE_ROWS), 32)
    return () => clearTimeout(id)
  }, [limit, rowTotal])
  const selectedIndex = selected ? list.findIndex(entry => entry.kind === 'row' && entry.item.key === selected) : -1
  const shownUntil = (() => { let rows = 0; for (let i = 0; i < list.length; i++) { if (list[i]!.kind === 'row' && ++rows > Math.max(limit, selectedIndex + 20)) return i } return list.length })()
  const fresh = new Map<string, number>()
  let cascade = 0
  for (const entry of list) if (entry.kind === 'row' && !seen.current.has(entry.item.key)) fresh.set(entry.item.key, firstPaint.current ? Math.min(cascade++, 14) * 14 : 0)
  useEffect(() => {
    if (!list.length) return
    for (const entry of list) if (entry.kind === 'row') seen.current.add(entry.item.key)
    firstPaint.current = false
  })
  useEffect(() => {
    const node = end.current
    if (!node || !mail.hasMore) return
    const observer = new IntersectionObserver(entries => { if (entries.some(entry => entry.isIntersecting)) onLoadMore() })
    observer.observe(node)
    return () => observer.disconnect()
  }, [mail.hasMore, onLoadMore, list.length])

  // Phone header, iOS style: a large title and two pills. Hidden on desktop, where the top bar has the tabs.
  const header = (
    <div className="m-header">
      <h1 className="m-title">{tab === 'important' ? 'Important' : 'Other'}</h1>
      <div className="m-subtitle">{status}</div>
      <div className="m-pills" role="tablist">
        <button role="tab" aria-selected={tab === 'important'} className={tab === 'important' ? 'on' : ''} onClick={() => onTab('important')}>Important<span>{importantCount}</span></button>
        <button role="tab" aria-selected={tab === 'other'} className={tab === 'other' ? 'on' : ''} onClick={() => onTab('other')}>Other<span>{otherCount}</span></button>
      </div>
    </div>
  )
  if (mail.status === 'loading') return <>{header}<div className="empty"><p className="muted">Loading your mail…</p></div></>
  if (!list.length) return <>{header}<Cleared mail={mail} tab={tab} /></>
  return (
    <div className={`list${cells ? ' cells' : ''}${tab === 'other' && !searching ? ' list-other' : ''}${searching ? ' list-search' : ''}`} role="listbox" aria-label={tab === 'important' ? 'Important' : 'Other'}>
      {header}
      {list.slice(0, shownUntil).map(entry => entry.kind === 'heading'
        ? <div key={`h:${entry.title}`} className="group fade-in">
            <span className={`group-title${entry.title.startsWith('✦') ? ' group-ask' : ''}`}>{entry.title}</span><span className="group-count">{entry.count}</span>
            {entry.note && <span className="group-note">{entry.note}</span>}
            {tab === 'other' && (entry.hidden || expanded.has(entry.title)) ? (
              <button className="group-more" onClick={() => onToggleGroup(entry.title)}>
                {expanded.has(entry.title) ? 'Show fewer' : `${entry.hidden} more`}<Kbd k="x" />
              </button>
            ) : null}
          </div>
        : <MailRow key={entry.item.key} item={entry.item} {...(entry.tier ? { tier: entry.tier } : {})} {...(entry.label !== undefined ? { label: entry.label } : {})} pending={!!entry.pending} selected={entry.item.key === selected} leaving={leaving.has(entry.item.key)} entering={fresh.has(entry.item.key)} delay={fresh.get(entry.item.key) ?? 0} density={density} handlers={handlers} />)}
      <div ref={end} className="list-end">{mail.hasMore ? 'Loading older mail…' : ''}</div>
    </div>
  )
}

function Cleared({ mail, tab }: { mail: MailState; tab: Tab }) {
  const importing = mail.accounts.some(account => !account.sync.lastSyncAt)
  if (importing) return <div className="empty"><h2>Bringing in your mail…</h2><p className="muted">New accounts take a minute. It shows up here as it arrives.</p></div>
  const other = partition(mail.conversations, mail.triage.enabled, mail.ownAddresses, mail.rules).otherCount
  return (
    <div className="empty">
      <h2>{tab === 'important' ? 'Nothing needs you.' : 'Other is clear.'}</h2>
      {tab === 'important' && <p className="muted">{other ? `${other} in Other, none urgent.` : 'You’re all caught up.'} <Kbd k="o" /> to look.</p>}
    </div>
  )
}

/** The right-hand context for whatever's selected: who it's from, your history with them, and what you opened recently. */
export function Sidebar({ mail, item, onOpenKey }: { mail: MailState; item: Conversation | null; onOpenKey: (key: string) => void }) {
  const prefs = usePrefs()
  const [history, setHistory] = useState<{ email: string; received: number; sent: number; conversations: number; recent: { key: string; subject: string; at: string }[] } | null>(null)
  const email = item?.lead.from.email.toLowerCase() ?? ''
  useEffect(() => {
    if (!email || !mail.mailboxes.length) { setHistory(null); return }
    let cancelled = false
    const mailboxIds = mail.mailboxes.slice(0, 50).map(mailbox => mailbox.id)
    void (async () => {
      try {
        const result = await inbox.mailboxCorrespondence({ mailboxIds, email, since: new Date().toISOString(), bucketMs: 30 * 86_400_000, bucketCount: 1, recentLimit: 5 })
        const keys = result.recent.filter(key => `${key.sourceId}:${key.threadId}` !== item?.key).slice(0, 4)
        const threads = keys.length ? (await inbox.mailboxConversations({ mailboxIds, keys })).items : []
        if (!cancelled) setHistory({
          email, received: result.received, sent: result.sent, conversations: result.conversations,
          recent: threads.map(thread => ({ key: `${thread.sourceId}:${thread.threadId}`, subject: thread.subject || '(no subject)', at: thread.lastMessageAt })),
        })
      } catch { if (!cancelled) setHistory(null) }
    })()
    return () => { cancelled = true }
  }, [email, item?.key, mail.mailboxes])

  if (!prefs.sidebar) return null
  const opens = recentOpens().filter(open => open.key !== item?.key).slice(0, 4)
  const from = item?.lead.from
  const d = item?.decision
  const relation = history && history.conversations
    ? [history.sent ? (history.sent >= 5 ? 'Frequent' : 'You’ve replied') : 'Never replied', `${history.conversations} thread${history.conversations === 1 ? '' : 's'}`].join(' · ')
    : ''
  return (
    <aside className="sidebar">
      {item && from ? (
        <section className="sender swap" key={item.key}>
          <Avatar name={from.name} email={from.email} person={d ? d.sender === 'person' : true} {...(item.account ? { brand: item.account.brand, tone: item.account.tone } : {})} size={48} />
          {(() => { const via = /^(.+?)\s+via\s+(.+)$/i.exec(from.name || ''); return <div className="sender-name">{via ? via[1] : from.name || from.email}{via && <span className="sender-via">via {via[2]}</span>}</div> })()}
          {from.name && <div className="muted small">{from.email}</div>}
          {item.account && <div className="muted small">Writes to your {item.account.email}</div>}
          {relation && <div className="faint small">{relation}</div>}
          {(() => { const h = mail.habits[from.email.toLowerCase()]; if (!h || h.opened + h.ignored < 2) return null
            return <div className="faint small">You opened {h.opened} of {Math.max(h.received, h.opened + h.ignored)}{h.replied ? ` · replied ${h.replied}` : ''}{h.ignored ? ` · cleared ${h.ignored} unread` : ''}</div> })()}
          {(() => { const check = senderCheck(from, d ? d.sender === 'person' : true); return check.mismatch
            ? <div className="sender-note small">Name says {check.claimed}, sent from {check.site}{check.via ? ` via ${check.via}` : ''}.</div> : null })()}
          {d && <div className="facts">
            <span>{d.label}</span>
            <span>{d.sender === 'person' ? 'A person' : d.sender === 'company' ? 'A company' : 'Automated'}</span>
            {d.important && <span>{d.critical ? 'Urgent' : d.urgency >= 0.5 ? 'Soon' : 'No rush'}</span>}
          </div>}
        </section>
      ) : <section className="sender"><div className="muted small">Select a conversation.</div></section>}
      {history?.recent.length ? (
        <section className="swap" key={`recent:${item?.key}`}>
          <h3><span>Recent with {(() => { const name = (from?.name || from?.email || '').replace(/\s+via\s+.+$/i, ''); return from?.name && (item?.decision?.sender ?? 'person') === 'person' ? name.split(' ')[0] : name })()}</span></h3>
          {history.recent.map(entry => (
            <button key={entry.key} className="side-row" onClick={() => onOpenKey(entry.key)}><span className="ellipsis">{entry.subject}</span><span className="faint">{age(entry.at)}</span></button>
          ))}
        </section>
      ) : null}
      {opens.length ? (
        <section>
          <h3>Recently opened <Kbd k="g r" /></h3>
          {opens.map((open, index) => (
            <button key={open.key} className="side-row" onClick={() => onOpenKey(open.key)}>
              <span className="faint key">{index + 1}</span>
              <span className="ellipsis">{open.sender} · {open.subject}</span>
              <span className="faint">{age(new Date(open.at).toISOString())}</span>
            </button>
          ))}
        </section>
      ) : null}
      <SyncNote mail={mail} />
    </aside>
  )
}

function SyncNote({ mail }: { mail: MailState }) {
  const importing = mail.accounts.filter(account => !account.sync.lastSyncAt || account.sync.coverage === 'empty')
  const problems = mail.accounts.filter(account => account.status === 'reconnect_required' || account.sync.problem)
  if (!importing.length && !mail.triage.pending && !problems.length) return null
  return (
    <section className="sync-note">
      {importing.map(account => <div key={account.id} className="small muted"><Tile brand={account.brand} tone={account.tone} size={12} /> Importing {account.email}…</div>)}
      {mail.triage.pending > 0 && <div className="small muted">Sorting {mail.triage.pending} email{mail.triage.pending === 1 ? '' : 's'}…</div>}
      {problems.map(account => <div key={account.id} className="small warn"><Tile brand={account.brand} tone={account.tone} size={12} /> {account.email} needs attention in Settings.</div>)}
    </section>
  )
}

export function AccountSwitcher({ accounts, accountId, onPick }: { accounts: AccountInfo[]; accountId: string | null; onPick: (id: string | null) => void }) {
  if (accounts.length < 2) return <div className="switcher">{accounts.map(account => <span key={account.id} className="switch on"><Tile brand={account.brand} size={14} />{account.email}</span>)}</div>
  return (
    <div className="switcher">
      <button className={`switch${accountId === null ? ' on' : ''}`} onClick={() => onPick(null)}><Kbd k="0" />All</button>
      {accounts.slice(0, 9).map(account => (
        <button key={account.id} className={`switch${accountId === account.id ? ' on' : ''}`} onClick={() => onPick(account.id)} title={account.email}>
          <Kbd k={account.number} /><Tile brand={account.brand} tone={account.tone} size={14} />{label(account, accounts)}
        </button>
      ))}
    </div>
  )
}

/** "Personal", "iCloud", or the address when two accounts share a provider. */
export function label(account: AccountInfo, accounts: AccountInfo[]): string {
  const names: Record<string, string> = { gmail: 'Gmail', icloud: 'iCloud', outlook: 'Outlook', fastmail: 'Fastmail', yahoo: 'Yahoo', imap: 'Mail', test: 'Test' }
  const same = accounts.filter(other => other.brand === account.brand).length > 1
  return same ? account.email.split('@')[0]! : names[account.brand] ?? account.email
}
